import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { MousePointerClick, Phone, Mail, ExternalLink, Calculator, Star } from "lucide-react";
import { subDays, startOfDay } from "date-fns";

interface ClickRow {
  link_label: string;
  link_url: string | null;
  link_category: string;
  page_path: string | null;
  clicked_at: string;
}

type Period = "day" | "week" | "month";

const CATEGORY_META: Record<string, { label: string; icon: React.ComponentType<{ className?: string }>; color: string }> = {
  phone: { label: "Telefon", icon: Phone, color: "bg-green-500/10 text-green-600 border-green-500/20" },
  email: { label: "E-Mail", icon: Mail, color: "bg-blue-500/10 text-blue-600 border-blue-500/20" },
  heyflow: { label: "Heyflow", icon: ExternalLink, color: "bg-purple-500/10 text-purple-600 border-purple-500/20" },
  provenexpert: { label: "ProvenExpert", icon: Star, color: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
  wizard_cta: { label: "Wizard CTA", icon: Calculator, color: "bg-primary/10 text-primary border-primary/20" },
};

const periodToDays: Record<Period, number> = { day: 1, week: 7, month: 30 };

const ClickAnalytics = () => {
  const [clicks, setClicks] = useState<ClickRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [period, setPeriod] = useState<Period>("day");

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      // Fetch last 30 days max — we filter client-side per tab
      const since = subDays(new Date(), 31).toISOString();
      const { data, error } = await supabase
        .from("link_clicks")
        .select("link_label, link_url, link_category, page_path, clicked_at")
        .gte("clicked_at", since)
        .order("clicked_at", { ascending: false })
        .limit(5000);
      if (!error && data) setClicks(data as ClickRow[]);
      setIsLoading(false);
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    const cutoff = startOfDay(subDays(new Date(), periodToDays[period] - 1));
    return clicks.filter((c) => new Date(c.clicked_at) >= cutoff);
  }, [clicks, period]);

  // Aggregate by category
  const byCategory = useMemo(() => {
    const map = new Map<string, number>();
    filtered.forEach((c) => {
      map.set(c.link_category, (map.get(c.link_category) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count);
  }, [filtered]);

  // Aggregate by individual link (label + url)
  const byLink = useMemo(() => {
    const map = new Map<string, { label: string; url: string | null; category: string; count: number }>();
    filtered.forEach((c) => {
      const key = `${c.link_category}::${c.link_label}::${c.link_url ?? ""}`;
      const existing = map.get(key);
      if (existing) {
        existing.count++;
      } else {
        map.set(key, {
          label: c.link_label,
          url: c.link_url,
          category: c.link_category,
          count: 1,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [filtered]);

  const totalClicks = filtered.length;

  const renderTab = () => (
    <div className="space-y-6">
      {/* Category overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {Object.entries(CATEGORY_META).map(([key, meta]) => {
          const count = byCategory.find((c) => c.category === key)?.count || 0;
          const Icon = meta.icon;
          return (
            <Card key={key}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">{meta.label}</span>
                </div>
                <div className="text-2xl font-bold">{count}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detailed link table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium">Einzelne Links ({byLink.length})</h4>
          <span className="text-sm text-muted-foreground">Gesamt: {totalClicks} Klicks</span>
        </div>
        {byLink.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground border rounded-lg">
            <MousePointerClick className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p>Keine Klicks im gewählten Zeitraum</p>
          </div>
        ) : (
          <div className="overflow-x-auto border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Kategorie</TableHead>
                  <TableHead>Link</TableHead>
                  <TableHead>Ziel</TableHead>
                  <TableHead className="text-right">Klicks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {byLink.map((link, i) => {
                  const meta = CATEGORY_META[link.category];
                  return (
                    <TableRow key={i}>
                      <TableCell>
                        <Badge variant="outline" className={meta?.color}>
                          {meta?.label || link.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs truncate font-medium">{link.label}</TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                        {link.url || "-"}
                      </TableCell>
                      <TableCell className="text-right font-bold">{link.count}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MousePointerClick className="h-5 w-5" />
          Link-Klicks
        </CardTitle>
        <CardDescription>
          Welche Links wurden wie oft geklickt (Telefon, E-Mail, Heyflow, ProvenExpert, Wizard-CTAs)
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <Tabs value={period} onValueChange={(v) => setPeriod(v as Period)}>
            <TabsList className="mb-6">
              <TabsTrigger value="day">Heute</TabsTrigger>
              <TabsTrigger value="week">7 Tage</TabsTrigger>
              <TabsTrigger value="month">30 Tage</TabsTrigger>
            </TabsList>
            <TabsContent value="day">{renderTab()}</TabsContent>
            <TabsContent value="week">{renderTab()}</TabsContent>
            <TabsContent value="month">{renderTab()}</TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
};

export default ClickAnalytics;
